import { BaseCommand } from './BaseCommand.js';
import * as fs from 'fs-extra';
import * as path from 'path';

export interface InitOptions {
	name?: string;
	template?: string;
	typescript?: boolean;
	eslint?: boolean;
	prettier?: boolean;
	pinia?: boolean;
	testing?: boolean;
	git?: boolean;
}

export class InitCommand extends BaseCommand {
	async execute(options: InitOptions): Promise<void> {
		const projectName = options.name || 'my-frame-app';
		const projectPath = path.join(this.cwd, projectName);

		this.log(`Initializing new Frame project: ${projectName}`);

		try {
			// Check if directory already exists
			if (await fs.pathExists(projectPath)) {
				throw new Error(`Directory ${projectName} already exists`);
			}

			// Create project directory
			await fs.ensureDir(projectPath);

			// Create basic project structure
			await this.createProjectStructure(projectPath, options);

			// Initialize git repository if requested
			if (options.git) {
				await this.initGitRepository(projectPath);
			}

			this.logSuccess(`Project ${projectName} created successfully!`);
			this.log(`Next steps:`);
			this.log(`  cd ${projectName}`);
			this.log(`  bun install`);
			this.log(`  frame dev`);

		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			this.logError(`Failed to initialize project: ${errorMessage}`);
			throw error;
		}
	}

	private async createProjectStructure(projectPath: string, options: InitOptions): Promise<void> {
		// Create package.json
		const packageJson = {
			name: path.basename(projectPath),
			version: '1.0.0',
			private: true,
			scripts: {
				dev: 'frame dev',
				build: 'frame build',
				preview: 'frame preview'
			},
			dependencies: {
				'@livx.cc/frame': '^0.1.0'
			},
			devDependencies: {} as Record<string, string>
		};

		// Add optional dependencies based on options
		if (options.pinia) {
			packageJson.dependencies['pinia'] = '^2.1.6';
		}

		if (options.testing) {
			packageJson.scripts['test'] = 'vitest';
			packageJson.devDependencies['vitest'] = '^0.34.0';
			packageJson.devDependencies['@vue/test-utils'] = '^2.4.0';
			packageJson.devDependencies['jsdom'] = '^22.1.0';
		}

		if (options.eslint) {
			packageJson.devDependencies['eslint'] = '^8.45.0';
			packageJson.devDependencies['@typescript-eslint/eslint-plugin'] = '^6.0.0';
			packageJson.devDependencies['@typescript-eslint/parser'] = '^6.0.0';
			packageJson.devDependencies['eslint-plugin-vue'] = '^9.15.0';
		}

		if (options.prettier) {
			packageJson.devDependencies['prettier'] = '^3.0.0';
		}

		// Clean up devDependencies if empty
		if (Object.keys(packageJson.devDependencies).length === 0) {
			delete (packageJson as any).devDependencies;
		}

		await fs.outputFile(
			path.join(projectPath, 'package.json'),
			JSON.stringify(packageJson, null, 2)
		);

		// Create directory structure
		await fs.ensureDir(path.join(projectPath, 'src', 'views'));
		await fs.ensureDir(path.join(projectPath, 'src', 'composables'));
		await fs.ensureDir(path.join(projectPath, 'src', 'components'));
		await fs.ensureDir(path.join(projectPath, 'src', 'assets'));
		await fs.ensureDir(path.join(projectPath, 'public'));

		// Create store directory if Pinia is enabled
		if (options.pinia) {
			await fs.ensureDir(path.join(projectPath, 'src', 'store'));
		}

		// Create tests directory if testing is enabled
		if (options.testing) {
			await fs.ensureDir(path.join(projectPath, 'tests', 'unit'));
			await fs.ensureDir(path.join(projectPath, 'tests', 'integration'));
		}

		// Create Frame configuration
		await this.createFrameConfig(projectPath);

		// Create basic views
		await this.createBasicViews(projectPath, options);

		// Create basic composable if requested
		await this.createBasicComposable(projectPath, options);

		// Create basic store if Pinia is enabled
		if (options.pinia) {
			await this.createBasicStore(projectPath, options);
		}

		// Create index.html in public
		await this.createIndexHtml(projectPath);

		// Create .gitignore
		await this.createGitignore(projectPath);

		// Create TypeScript configuration if enabled
		if (options.typescript) {
			await this.createTsConfig(projectPath);
		}

		// Create ESLint configuration if enabled
		if (options.eslint) {
			await this.createEslintConfig(projectPath);
		}

		// Create Prettier configuration if enabled
		if (options.prettier) {
			await this.createPrettierConfig(projectPath);
		}

		// Create Vitest configuration if testing is enabled
		if (options.testing) {
			await this.createVitestConfig(projectPath);
		}
	}

	private async createFrameConfig(projectPath: string): Promise<void> {
		const config = `export default {
  // Pug templating configuration
  pug: {
    pretty: true,
    globals: {
      // Global variables available in all Pug templates
    },
    filters: {
      // Custom Pug filters
    }
  },

  // Meta management configuration
  meta: {
    title: 'Frame App',
    description: 'A Vue.js application built with Frame',
    keywords: ['vue', 'frame', 'javascript'],
    author: 'Frame Developer',
    
    // Open Graph meta tags
    openGraph: {
      siteName: 'Frame App',
      type: 'website',
      locale: 'en_US',
      // image: '/og-image.jpg',
      // url: 'https://your-site.com'
    },
    
    // Twitter Card meta tags
    twitter: {
      card: 'summary_large_image',
      // site: '@yoursite',
      // creator: '@yourcreator'
    }
  },

  // Build configuration
  build: {
    target: 'static',
    outDir: 'dist',
  },

  // Development server configuration
  server: {
    port: 3000,
    host: 'localhost',
    open: true
  }
};
`;

		await fs.outputFile(path.join(projectPath, 'frame.config.mjs'), config);
	}

	private async createBasicViews(projectPath: string, options: InitOptions): Promise<void> {
	// Create Home view
		const homeVue = `<template lang="pug">
.home
  h1 Welcome to Frame!
  p Your Vue.js app is running with Frame infrastructure.
  p Edit this file at 
    code src/views/Home.vue

  .features
    h2 Features
    ul
      li ✅ Pug templating
      li ✅ Dynamic meta management
      li ✅ File-based routing
      li ✅ TypeScript support
      ${options.pinia ? 'li ✅ Pinia state management' : ''}
      ${options.testing ? 'li ✅ Vitest testing' : ''}
      ${options.eslint ? 'li ✅ ESLint configuration' : ''}
      ${options.prettier ? 'li ✅ Prettier formatting' : ''}
</template>

<script setup${options.typescript ? ' lang="ts"' : ''}>
import { useFrameMeta } from '#frame/meta-helpers'

// Set page meta
useFrameMeta({
  title: 'Home - Frame App',
  description: 'Welcome to your new Frame application'
})

// Your component logic here
</script>

<style scoped>
.home {
  text-align: center;
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

h1 {
  color: #42b883;
  margin-bottom: 1rem;
  font-size: 2.5rem;
}

h2 {
  color: #2c3e50;
  margin: 2rem 0 1rem;
}

.features {
  text-align: left;
  margin-top: 2rem;
}

ul {
  list-style: none;
  padding: 0;
}

li {
  padding: 0.5rem 0;
  font-size: 1.1rem;
}

code {
  background-color: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: 'Courier New', monospace;
}
</style>
`;

		await fs.outputFile(
			path.join(projectPath, 'src', 'views', 'Home.vue'),
			homeVue
		);

		// Create About view
		const aboutVue = `<template lang="pug">
.about
  h1 About Frame
  p Frame is a Vue.js infrastructure framework that provides:
  
  .features-grid
    .feature-card
      h3 🎨 Pug Templating
      p Clean, concise template syntax
    
    .feature-card
      h3 🏷️ Meta Management
      p Dynamic meta tags and SEO
    
    .feature-card
      h3 🚀 File-based Routing
      p Automatic routing from file structure
    
    .feature-card
      h3 📦 TypeScript Ready
      p Full TypeScript support out of the box
</template>

<script setup${options.typescript ? ' lang="ts"' : ''}>
import { useFrameMeta } from '#frame/meta-helpers'

// Set page meta
useFrameMeta({
  title: 'About - Frame App',
  description: 'Learn about Frame and its features'
})
</script>

<style scoped>
.about {
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

h1 {
  color: #42b883;
  margin-bottom: 1rem;
  text-align: center;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.feature-card {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.feature-card h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.feature-card p {
  color: #6c757d;
  line-height: 1.6;
}
</style>
`;

		await fs.outputFile(
			path.join(projectPath, 'src', 'views', 'About.vue'),
			aboutVue
		);
	}

	private async createBasicComposable(projectPath: string, options: InitOptions): Promise<void> {
		const composable = `import { ref, computed } from 'vue'

export function useCounter(initialValue = 0) {
  const count = ref(initialValue)
  
  const doubleCount = computed(() => count.value * 2)
  
  const increment = () => {
    count.value++
  }
  
  const decrement = () => {
    count.value--
  }
  
  const reset = () => {
    count.value = initialValue
  }
  
  return {
    count,
    doubleCount,
    increment,
    decrement,
    reset
  }
}
`;

		await fs.outputFile(
			path.join(projectPath, 'src', 'composables', 'useCounter.ts'),
			composable
		);
	}

	private async createBasicStore(projectPath: string, options: InitOptions): Promise<void> {
		const store = `import { defineStore } from 'pinia'

export const useMainStore = defineStore('main', {
  state: () => ({
    count: 0,
    name: 'Frame App'
  }),
  
  getters: {
    doubleCount: (state) => state.count * 2,
    greeting: (state) => \`Hello from \${state.name}!\`
  },
  
  actions: {
    increment() {
      this.count++
    },
    
    decrement() {
      this.count--
    },
    
    updateName(newName: string) {
      this.name = newName
    }
  }
})
`;

		await fs.outputFile(
			path.join(projectPath, 'src', 'store', 'index.ts'),
			store
		);
	}

	private async createIndexHtml(projectPath: string): Promise<void> {
		const indexHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Frame App</title>
  <meta name="description" content="A Vue.js application built with Frame">

  <!-- Open Graph -->
  <meta property="og:type" content="website">
  <meta property="og:site_name" content="Frame App">
  <meta property="og:title" content="Frame App">
  <meta property="og:description" content="A Vue.js application built with Frame">

  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="Frame App">
  <meta name="twitter:description" content="A Vue.js application built with Frame">

  <!-- Theme -->
  <meta name="theme-color" content="#42b883">

  <!-- Favicon -->
  <link rel="icon" href="/favicon.ico" type="image/x-icon">
</head>
<body>
  <div id="app"></div>
</body>
</html>
`;

		await fs.outputFile(
			path.join(projectPath, 'public', 'index.html'),
			indexHtml
		);

		// Create a basic favicon placeholder
		await fs.outputFile(
			path.join(projectPath, 'public', 'favicon.ico'),
			'<!-- Favicon placeholder -->'
		);
	}

	private async createGitignore(projectPath: string): Promise<void> {
		const gitignore = `# Dependencies
node_modules/
npm-debug.log*
yarn-debug.log*
yarn-error.log*
pnpm-debug.log*

# Build outputs
dist/
build/

# Frame generated files
src/.frame/

# Environment files
.env
.env.local
.env.development.local
.env.test.local
.env.production.local

# IDE files
.vscode/
.idea/
*.swp
*.swo
*~

# OS files
.DS_Store
Thumbs.db

# Logs
*.log
logs/

# Testing
coverage/
.nyc_output/

# Temporary files
.tmp/
temp/
`;

		await fs.outputFile(
			path.join(projectPath, '.gitignore'),
			gitignore
		);
	}

	private async createTsConfig(projectPath: string): Promise<void> {
		const tsconfig = {
			compilerOptions: {
				target: "ES2020",
				useDefineForClassFields: true,
				lib: ["ES2020", "DOM", "DOM.Iterable"],
				module: "ESNext",
				skipLibCheck: true,
				moduleResolution: "bundler",
				allowImportingTsExtensions: true,
				resolveJsonModule: true,
				isolatedModules: true,
				noEmit: true,
				jsx: "preserve",
				strict: true,
				noUnusedLocals: true,
				noUnusedParameters: true,
				noFallthroughCasesInSwitch: true,
				baseUrl: ".",
				types: [
					"vite/client"
				],
				paths: {
					"@/*": ["src/*"],
					"@views/*": ["src/views/*"],
					"@components/*": ["src/components/*"],
					"@composables/*": ["src/composables/*"],
					"@store/*": ["src/store/*"],
					"#frame/*": ["src/.frame/*"]
				}
			},
			include: [
				"src/**/*.ts",
				"src/**/*.tsx",
				"src/**/*.vue",
				"src/.frame/**/*"
			],
			exclude: [
				"node_modules",
				"dist"
			]
		};

		await fs.outputFile(path.join(projectPath, 'tsconfig.json'), JSON.stringify(tsconfig, null, 2));
	}

	private async createEslintConfig(projectPath: string): Promise<void> {
		const eslintConfig = `module.exports = {
  root: true,
  env: {
    node: true,
    es2021: true,
  },
  extends: [
    'eslint:recommended',
    '@vue/eslint-config-typescript',
    'plugin:vue/vue3-essential',
  ],
  parserOptions: {
    ecmaVersion: 'latest',
    sourceType: 'module',
  },
  rules: {
    'no-console': process.env.NODE_ENV === 'production' ? 'warn' : 'off',
    'no-debugger': process.env.NODE_ENV === 'production' ? 'warn' : 'off',
    'vue/multi-word-component-names': 'off',
  },
}
`;

		await fs.outputFile(path.join(projectPath, '.eslintrc.js'), eslintConfig);
	}

	private async createPrettierConfig(projectPath: string): Promise<void> {
		const prettierConfig = {
			semi: true,
			trailingComma: 'es5',
			singleQuote: true,
			printWidth: 100,
			tabWidth: 2,
			useTabs: false,
		};

		await fs.outputFile(path.join(projectPath, '.prettierrc'), JSON.stringify(prettierConfig, null, 2));
	}

	private async createVitestConfig(projectPath: string): Promise<void> {
		const vitestConfig = `import { defineConfig } from 'vitest/config'
import vue from '@vitejs/plugin-vue'
import path from 'path'

export default defineConfig({
  plugins: [vue()],
  test: {
    globals: true,
    environment: 'jsdom',
  },
  resolve: {
    alias: {
      '@': path.resolve(__dirname, 'src'),
      '#frame': path.resolve(__dirname, 'src/.frame'),
    },
  },
})
`;

		await fs.outputFile(path.join(projectPath, 'vitest.config.ts'), vitestConfig);
	}

	private async initGitRepository(projectPath: string): Promise<void> {
		const { execSync } = await import('child_process');

		try {
			execSync('git init', { cwd: projectPath, stdio: 'inherit' });
			execSync('git add .', { cwd: projectPath, stdio: 'inherit' });
			execSync('git commit -m "Initial commit"', { cwd: projectPath, stdio: 'inherit' });
			this.log('✓ Git repository initialized');
		} catch (error) {
			this.log('⚠ Failed to initialize git repository');
		}
	}
} 